/**
 * AuthAPIHook.tsx
 *
 * Adds an Axios hook that adds the Auth0 bearer token to all requests made to Express API.
 * This should wrap everything that needs to access the secure Express API,
 * and itself should be eventually wrapped by a Auth0Provider (because it uses useAuth0()).
 *
 */

import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";

const AuthAPIHook = ({ children }: { children: JSX.Element }) => {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } =
    useAuth0();

  // Use refs so the interceptors always see the latest values without
  // needing to be re-registered on every render.
  const authRef = useRef({
    getAccessTokenSilently,
    loginWithRedirect,
    isAuthenticated,
  });
  useEffect(() => {
    authRef.current = {
      getAccessTokenSilently,
      loginWithRedirect,
      isAuthenticated,
    };
  });

  useEffect(() => {
    // Request interceptor: attach bearer token to every outgoing request.
    const requestId = axios.interceptors.request.use(
      async (config) => {
        if (authRef.current.isAuthenticated) {
          try {
            const token = await authRef.current.getAccessTokenSilently({
              authorizationParams: {
                audience: import.meta.env.VITE_AUTH0_AUDIENCE,
              },
            });

            if (token && config.headers) {
              config.headers.authorization = `Bearer ${token}`;
            }
          } catch (error: unknown) {
            console.error("Failed to get access token:", error);

            // If the refresh token is expired, missing, or revoked, Auth0
            // throws "login_required", "consent_required",
            // "missing_refresh_token", or "invalid_grant". Redirect to login
            // so the user can re-authenticate instead of silently sending
            // the request without a token.
            const authError = error as { error?: string } | undefined;
            if (
              authError?.error === "login_required" ||
              authError?.error === "consent_required" ||
              authError?.error === "missing_refresh_token" ||
              authError?.error === "invalid_grant"
            ) {
              await authRef.current.loginWithRedirect({
                appState: { returnTo: globalThis.window.location.pathname },
              });
            }
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor: catch 401s as a safety net in case a request
    // slips through with a stale token.
    const responseId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && authRef.current.isAuthenticated) {
          console.error("Received 401 — redirecting to login.");
          await authRef.current.loginWithRedirect({
            appState: { returnTo: globalThis.window.location.pathname },
          });
        }
        return Promise.reject(error);
      },
    );

    // Cleanup: eject interceptors when the component unmounts so they
    // are not duplicated on re-mount / hot-reload.
    return () => {
      axios.interceptors.request.eject(requestId);
      axios.interceptors.response.eject(responseId);
    };
  }, []); // Register once on mount.

  return children;
};

export default AuthAPIHook;
